'use client';

/**
 * @file AuthContext — 인증 상태 관리 Context
 *
 * JWT 기반 인증 상태(user, isAuthenticated, isLoading)를 앱 전체에 제공한다.
 * 로그인·회원가입·로그아웃 액션과 토큰 자동 갱신(refresh) 로직을 포함하며,
 * 하위 컴포넌트는 `useAuth()` 훅을 통해 인증 상태와 액션에 접근한다.
 *
 * - Access Token: 메모리에 보관 (setAccessToken)
 * - Refresh Token: HttpOnly 쿠키로 서버에서 관리
 * - 마운트 시 refresh token으로 세션 복원을 시도한다.
 */

import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';
import { apiFetch, ApiError } from '@/lib/api';
import { setAccessToken, clearTokens, clearRefreshCookie, refreshAccessToken } from '@/lib/auth';
import { API_ENDPOINTS } from '@/lib/constants';
import type { User, LoginResponse, SignupRequest } from '@/lib/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  isAuthTransitioning: boolean;
  subscribeAuthInvalidation: (listener: () => void) => () => void;
  login: (identifier: string, password: string) => Promise<LoginResponse>;
  signup: (data: SignupRequest) => Promise<{ approved: boolean; message?: string }>;
  logout: () => Promise<void>;
  updateUser: (data: Partial<User>) => void;
  refreshUser: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();

  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthTransitioning, setIsAuthTransitioning] = useState(false);
  const transitions = useRef(0);
  const invalidationListeners = useRef(new Set<() => void>());
  const subscribeAuthInvalidation = useCallback((listener: () => void) => {
    invalidationListeners.current.add(listener);
    return () => {
      invalidationListeners.current.delete(listener);
    };
  }, []);
  const beginAuthTransition = useCallback(() => {
    transitions.current += 1;
    setIsAuthTransitioning(true);
    for (const listener of invalidationListeners.current) {
      try {
        listener();
      } catch {
        /* One subscriber must not prevent other state from being cleared. */
      }
    }
  }, []);
  const endAuthTransition = useCallback(() => {
    transitions.current -= 1;
    setIsAuthTransitioning(transitions.current > 0);
  }, []);

  // ---- helpers ------------------------------------------------------------

  const fetchUserProfile = useCallback(async (): Promise<User> => {
    const profile = await apiFetch<User>(API_ENDPOINTS.USER_ME);
    setUser(profile);
    setIsAuthenticated(true);
    return profile;
  }, []);

  // ---- 마운트 시 세션 복원 (refresh token → access token → 프로필 조회) ------

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        // HttpOnly 쿠키의 refresh token으로 새 access token 발급 시도
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          return; // 기존 세션 없음
        }

        if (!cancelled) {
          await fetchUserProfile();
        }
      } catch {
        // 갱신 또는 프로필 조회 실패 — 미인증 상태로 처리
        if (!cancelled) {
          setIsAuthenticated(false);
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    init();

    // StrictMode 중복 실행 방지용 cleanup
    return () => {
      cancelled = true;
    };
  }, [fetchUserProfile]);

  // ---- 인증 액션 (로그인 / 회원가입 / 로그아웃) -----------------------------

  const login = useCallback(
    async (identifier: string, password: string): Promise<LoginResponse> => {
      beginAuthTransition();
      try {
        const response = await apiFetch<LoginResponse>(API_ENDPOINTS.AUTH_LOGIN, {
          method: 'POST',
          body: { identifier, password },
        });

        // 정상 로그인 — 토큰 저장 후 사용자 프로필 로드
        if (response.accessToken) {
          setAccessToken(response.accessToken);
          await fetchUserProfile();
        }

        return response;
      } finally {
        endAuthTransition();
      }
    },
    [fetchUserProfile, beginAuthTransition, endAuthTransition],
  );

  const signup = useCallback(
    async (data: SignupRequest): Promise<{ approved: boolean; message?: string }> => {
      beginAuthTransition();
      try {
        const response = await apiFetch<{
          approved: boolean;
          message?: string;
          accessToken?: string;
          user?: { id: string; name: string; email: string; avatarUrl?: string };
        }>(API_ENDPOINTS.AUTH_SIGNUP, {
          method: 'POST',
          body: data as unknown as Record<string, unknown>,
        });

        // 즉시 승인된 경우에만 토큰 저장 및 프로필 로드 (관리자 승인 대기 시 skip)
        if (response.approved && response.accessToken) {
          setAccessToken(response.accessToken);
          await fetchUserProfile();
        }

        return { approved: response.approved, message: response.message };
      } finally {
        endAuthTransition();
      }
    },
    [fetchUserProfile, beginAuthTransition, endAuthTransition],
  );

  const logout = useCallback(async (): Promise<void> => {
    beginAuthTransition();
    try {
      try {
        await apiFetch<void>(API_ENDPOINTS.AUTH_LOGOUT, {
          method: 'POST',
        });
      } catch {
        // 서버 호출 실패 시에도 로컬 상태는 반드시 초기화
      } finally {
        clearTokens();
        await clearRefreshCookie();
        setUser(null);
        setIsAuthenticated(false);
        router.push('/login');
      }
    } finally {
      endAuthTransition();
    }
  }, [router, beginAuthTransition, endAuthTransition]);

  const updateUser = useCallback((data: Partial<User>): void => {
    setUser((prev) => (prev ? { ...prev, ...data } : null));
  }, []);

  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      await fetchUserProfile();
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message);
      }
    }
  }, [fetchUserProfile]);

  // ---- memoised context value ---------------------------------------------

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      isAuthTransitioning,
      subscribeAuthInvalidation,
      login,
      signup,
      logout,
      updateUser,
      refreshUser,
    }),
    [
      user,
      isAuthenticated,
      isLoading,
      isAuthTransitioning,
      subscribeAuthInvalidation,
      login,
      signup,
      logout,
      updateUser,
      refreshUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
